import classnames from "classnames";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, Col, Modal, ModalBody, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";

import defaultTrxImg from "@/assets/images/default_transaction.png";
import { Badge } from "@/components/Common/Badge";
import { ApproveButton, CancelButton, RejectButton, ReturnButton } from "@/components/Common/Buttons";
import { DateTimeLabel } from "@/components/Common/DateTimeLabel";
import { DatePickerField, EmailInputField, InputField, MobileInputField } from "@/components/Common/Inputs";
import { PhotoViewer } from "@/components/Common/PhotoViewer";
import useCustomHook from "@/components/Hooks/useCustomHook";

import ActionModal from "./ActionModal";
import FileHistory from "./FileHistory";
import History from "./History";

import { useFindPatientEnrollmentsQuery } from "@/api/Endpoints/Globo/Transactions/PatientEnrollment/PatientEnrollments";

const profileBg = require(`@/${process.env.REACT_APP_CUSTOMER_USER_PROFILE}`);

const ViewDetails = ({ data, show = false, onCloseClick, parentKey, onUpdateParentKey }) => {
  const { state, customFunction } = useCustomHook({
    historyKey: 0,
    historyFilesKey: 0,
  });
  const [activeTab, setActiveTab] = useState("1");

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const { data: transactionData, refetch } = useFindPatientEnrollmentsQuery(
    { patientEnrollmentId: data?.patientEnrollmentId },
    { skip: !data?.patientEnrollmentId, refetchOnMountOrArgChange: show },
  );
  const findData = transactionData?.returnData;
  const { reset, control } = useForm({ defaultValues: {} });

  useEffect(() => {
    reset({
      ...data,
      ...findData,
    });
    customFunction.updateState({
      data: {
        ...data,
        ...findData,
      },
    });
  }, [show, data, findData, refetch, reset]);

  const handleActions = (bool, data) => {
    if (bool) {
      customFunction.updateToggle("toggleAction");
      customFunction.updateState({
        historyKey: state?.historyKey + 1,
        historyFilesKey: state?.historyFilesKey + 1,
      });
      onUpdateParentKey(parentKey + 1);
    } else {
      customFunction.updateToggle("toggleAction", { ...findData, actionName: data });
    }
  };

  return (
    <React.Fragment>
      <ActionModal
        state={state}
        customFunction={customFunction}
        patientEnrollmentId={findData?.patientEnrollmentId}
        data={state?.trxValue}
        show={state.toggle.toggleAction}
        onCloseClick={handleActions}
      />

      <Modal
        className="modal-fullscreen"
        modalClassName="flip"
        id="requestDetails"
        size="xl"
        isOpen={show}
        toggle={onCloseClick}
        centered
        backdrop="static">
        <ModalBody>
          <div>
            <div className="profile-foreground position-relative mx-n4 mt-n4">
              <div className="profile-wid-bg">
                <picture>
                  <source srcSet={profileBg} type="image/jpeg" />
                  <img src={profileBg} alt="" className="profile-wid-img" />
                </picture>
              </div>
              <button
                type="button"
                className=" btn btn-icon btn-sm btn-ghost-danger fs-16 position-absolute top-0 end-0 m-3 d-lg-none "
                style={{ zIndex: 1055 }}
                onClick={onCloseClick}>
                X
              </button>
            </div>
            <div className="pt-4 mb-4 mb-lg-3 pb-lg-4">
              <Row className="g-4">
                <div className="col-md-auto">
                  <PhotoViewer>
                    <img src={defaultTrxImg} style={{ width: "100px", height: "80px", cursor: "pointer" }} />
                  </PhotoViewer>
                </div>

                <Col style={{ paddingLeft: "0px" }}>
                  <div>
                    <h3 className="text-white mb-1">Patient Enrollment Request Details</h3>
                    <p className="text-white text-opacity-75">
                      Date {state.data?.statusName}:{" "}
                      <span className="fw-semibold">{<DateTimeLabel value={state.data?.statusDate} /> || "Draft"}</span>{" "}
                      <Badge value={state.data?.statusName} color={state.data?.statusColor} />
                      <br />
                      Reference No. :{" "}
                      <span className="fs-16 fw-semibold" style={{ color: "#0ab39c" }}>
                        {findData?.referenceNo}
                      </span>
                    </p>
                  </div>
                </Col>

                <Col xs={12} lg="auto" className="order-last order-lg-0 d-none d-lg-flex align-items-start">
                  <button type="button" className="btn btn-icon btn-sm btn-ghost-danger fs-16 mt-4 mt-lg-0" onClick={onCloseClick}>
                    X
                  </button>
                </Col>
              </Row>
            </div>

            <Row>
              <Col xxl={12}>
                <Card>
                  <CardBody>
                    <Row>
                      <Col lg={6}>
                        <Card>
                          <CardBody>
                            <Row className="align-items-center gy-2">
                              <Col lg={8} xs={12}>
                                <h5 className="card-title mb-0 text-center text-lg-start">Request Details</h5>
                              </Col>

                              <Col lg={4} xs={12}>
                                <div className=" d-flex justify-content-center justify-content-lg-end gap-1 mt-2 mt-lg-0 ">
                                  <ReturnButton isShowButton={state.data?.actions?.return} onClick={() => handleActions(false, "Return")} />
                                  <ApproveButton isShowButton={state.data?.actions?.approve} onClick={() => handleActions(false, "Approve")} />
                                  <RejectButton isShowButton={state.data?.actions?.reject} onClick={() => handleActions(false, "Reject")} />
                                  <CancelButton isShowButton={state.data?.actions?.cancel} onClick={() => handleActions(false, "Cancel")} />
                                </div>
                              </Col>
                            </Row>
                            <br />
                            <Row className="mt-2">
                              <Col lg={4}>
                                <InputField disabled name="patientCode" control={control} label="Patient Code" type="text" uppercase />
                              </Col>

                              <Col lg={8}>
                                <InputField disabled name="programName" control={control} label="Program Name" type="text" />
                              </Col>

                              <Col lg={4}>
                                <InputField disabled name="firstName" control={control} label="First Name" type="text" uppercase />
                              </Col>

                              <Col lg={3}>
                                <InputField disabled name="middleName" control={control} label="Middle Name" type="text" uppercase />
                              </Col>

                              <Col lg={4}>
                                <InputField disabled name="lastName" control={control} label="Last Name" type="text" uppercase />
                              </Col>

                              <Col lg={1}>
                                <InputField disabled name="suffix" control={control} label="Suffix" type="text" maxLength={4} uppercase />
                              </Col>

                              <Col lg={4}>
                                <DatePickerField disabled control={control} name="birthDate" label="Birth Date" />
                              </Col>

                              <Col lg={3}>
                                <InputField disabled name="gender" control={control} label="Gender" type="text" uppercase />
                              </Col>

                              <Col lg={5} />

                              <Col lg={4}>
                                <EmailInputField disabled name="emailAddress" control={control} label="Email Address" type="email" />
                              </Col>

                              <Col lg={4}>
                                <MobileInputField disabled name="mobileNo" control={control} label="Mobile No." type="text" />
                              </Col>

                              <Col lg={4} />

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  name="secondaryContactName"
                                  control={control}
                                  label="Secondary Contact Name"
                                  type="text"
                                />
                              </Col>

                              <Col lg={4}>
                                <MobileInputField
                                  disabled
                                  name="secondaryContactNo"
                                  control={control}
                                  label="Secondary Contact No."
                                />
                              </Col>

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="secondaryRelationshipToPatient"
                                  label="Relationship To Patient"
                                  type="text"
                                />
                              </Col>

                              <Col lg="12" className="mt-2">
                                <h5 className="text-bold text-success">Address Information</h5>
                                <hr className="pb-2" />
                              </Col>

                              <Col lg={6}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="province"
                                  label="Province"
                                  type="text"
                                />
                              </Col>

                              <Col lg={6}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="city"
                                  label="City"
                                  type="text"
                                />
                              </Col>

                              <Col lg={6}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="barangay"
                                  label="Barangay"
                                  type="text"
                                />
                              </Col>

                              <Col lg={6}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="streetAddress"
                                  label="Street Address"
                                  type="text"
                                />
                              </Col>

                              <Col lg="12" className="mt-2">
                                <h5 className="text-bold text-success">Medical Information</h5>
                                <hr className="pb-2" />
                              </Col>

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="doctorName"
                                  label="Doctor"
                                  type="text"
                                />
                              </Col>

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="therapeuticAreaName"
                                  label="Therapeutic Area"
                                  type="text"
                                />
                              </Col>

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="hospitalName"
                                  label="Hospital"
                                  type="text"
                                />
                              </Col>

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="diagnosisName"
                                  label="Diagnosis"
                                  type="text"
                                />
                              </Col>

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="treatmentLine"
                                  label="Treatment Line"
                                  type="text"
                                />
                              </Col>

                              <Col lg={4}>
                                <InputField
                                  disabled
                                  control={control}
                                  name="patientSegmentName"
                                  label="Patient Segment"
                                  type="text"
                                />
                              </Col>

                              <Col lg={12}>
                                <InputField
                                  disabled
                                  rows={4}
                                  name="remarks"
                                  control={control}
                                  label="Remarks"
                                  type="textarea"
                                  placeholder="Remarks here..."
                                />
                              </Col>

                              <div className="col-12">
                                <hr className="pb-2" />
                              </div>

                              <Col lg={6}>
                                <DatePickerField control={control} name="dateSubmitted" label="Date Submitted" disabled />
                              </Col>
                              <Col lg={6}>
                                <InputField control={control} name="submittedByName" label="Submitted By" disabled />
                              </Col>

                              {findData?.statusName !== "Submitted" ? (
                                <>
                                  <Col lg={6}>
                                    <DatePickerField control={control} name="statusDate" label={`Date ${findData?.statusName}`} disabled />
                                  </Col>
                                  <Col lg={6}>
                                    <InputField name="statusByName" control={control} label={`${findData?.statusName} By`} type="text" disabled />
                                  </Col>
                                </>
                              ) : null}
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col lg={6}>
                        <Nav className="nav-tabs nav-tabs-custom nav-success cursor-pointer" role="tablist">
                          <NavItem>
                            <NavLink onClick={() => toggleTab("1")} className={classnames({ active: activeTab === "1" }, "fw-medium")}>
                              History
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink onClick={() => toggleTab("2")} className={classnames({ active: activeTab === "2" }, "fw-medium")}>
                              Files
                            </NavLink>
                          </NavItem>
                        </Nav>
                        <br />
                        <TabContent activeTab={activeTab}>
                          <TabPane tabId="1">
                            <History key={state.historyKey} parentId={data?.patientEnrollmentId} />
                          </TabPane>
                          <TabPane tabId="2">
                            {activeTab === "2" && <FileHistory key={state.fileHistoryKey} patientEnrollmentId={findData?.patientEnrollmentId} />}
                          </TabPane>
                        </TabContent>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ViewDetails;
